import { cloneDeep } from 'lodash'
import { MissionFormValues } from '../../missions/mission-form'
import { addDays, differenceInDays, isBefore, subDays } from 'date-fns'
import { dateToTimeInputValue, formatISODate, timeInputValueToDate } from '../../../utils/date'

/** Minimal day shape for copy/paste logic (isoDate + optional start/end dates). */
export interface DayWithIsoDate {
  isoDate: string
  data?: {
    start?: { date: Date }
    end?: { date: Date }
  } | null
}

/** Time pair as stored in clipboard for one day (HH:mm format). */
export type TimePair = [string, string]

/** One slot in a week copy payload: either a time pair or null if day has no work period. */
export type WeekCopySlot = TimePair | null

export const updateWorkPeriod = (
  workPeriods: MissionFormValues['workPeriods'],
  isoDate: string,
  value: Partial<MissionFormValues['workPeriods'][0]>,
): MissionFormValues['workPeriods'] => {
  const existingWorkPeriodIndex = getExistingWorkPeriodIndex(workPeriods, isoDate)
  const existingWorkPeriod =
    existingWorkPeriodIndex === -1 ? undefined : cloneDeep(workPeriods[existingWorkPeriodIndex])

  const newWorkPeriod: Partial<MissionFormValues['workPeriods'][number]> = existingWorkPeriod || {}

  const startDate = value.start?.date
  const endDate = value.end?.date

  if (startDate) {
    newWorkPeriod.start = { date: startDate }
  }
  if (endDate) {
    newWorkPeriod.end = { date: endDate }
  }
  if (newWorkPeriod.end?.date && newWorkPeriod.start?.date) {
    if (isBefore(newWorkPeriod.end.date, newWorkPeriod.start.date)) {
      newWorkPeriod.end = { date: addDays(newWorkPeriod.end.date, 1) }
    }
    if (differenceInDays(newWorkPeriod.end.date, newWorkPeriod.start.date) > 0) {
      newWorkPeriod.end = { date: subDays(newWorkPeriod.end.date, 1) }
    }
  }
  const newWorkPeriods = cloneDeep(workPeriods)
  if (existingWorkPeriod) {
    newWorkPeriods[existingWorkPeriodIndex] = newWorkPeriod
  } else {
    newWorkPeriods.push(newWorkPeriod)
  }
  return newWorkPeriods.sort((a, b) => {
    const wP1Date = a.start?.date ?? a.end?.date
    const wP2Date = b.start?.date ?? b.end?.date

    return wP1Date!.getTime() - wP2Date!.getTime()
  })
}

export const removeWorkPeriod = (
  workPeriods: MissionFormValues['workPeriods'],
  isoDate: string,
): MissionFormValues['workPeriods'] => {
  const newWorkPeriods = cloneDeep(workPeriods)

  return newWorkPeriods
    .filter(wP => {
      const wPDate = wP.start?.date ?? wP.end?.date
      const wPDateISO = formatISODate(new Date(wPDate!))
      if (wPDateISO === isoDate) return false
      return true
    })
    .sort((a, b) => {
      const wP1Date = a.start?.date ?? a.end?.date
      const wP2Date = b.start?.date ?? b.end?.date
      return wP1Date!.getTime() - wP2Date!.getTime()
    })
}

export const updateWorkPeriods = (
  workPeriods: MissionFormValues['workPeriods'],
  isoDates: string[],
  values: Partial<MissionFormValues['workPeriods']>,
): MissionFormValues['workPeriods'] => {
  let newWorkPeriods: MissionFormValues['workPeriods'] = cloneDeep(workPeriods)
  let index = 0
  for (const isoDate of isoDates) {
    const value = values[index]
    if (value === undefined) {
      newWorkPeriods = removeWorkPeriod(newWorkPeriods, isoDate)
    } else {
      newWorkPeriods = updateWorkPeriod(newWorkPeriods, isoDate, value)
    }
    index++
  }
  return newWorkPeriods
}

const getExistingWorkPeriodIndex = (
  workPeriods: MissionFormValues['workPeriods'],
  isoDate: string,
): number => {
  const existingWorkPeriodIndex = workPeriods.findIndex(wP => {
    const date = wP.start?.date ?? wP.end?.date
    if (!date) return false
    const wPDateISO = formatISODate(new Date(date))
    return wPDateISO === isoDate
  })
  return existingWorkPeriodIndex
}

export const getExistingWorkPeriod = (
  workPeriods: MissionFormValues['workPeriods'],
  isoDate: string,
): MissionFormValues['workPeriods'][0] | undefined => {
  const index = getExistingWorkPeriodIndex(workPeriods, isoDate)
  if (index === undefined) return undefined
  return workPeriods[index]
}

/** Build work periods after pasting a day value to all given days (e.g. one weekday column). */
export const getWorkPeriodsAfterPasteToWeekday = (
  workPeriods: MissionFormValues['workPeriods'],
  days: { isoDate: string }[],
  clipboardDayValue: [string | undefined, string | undefined],
): MissionFormValues['workPeriods'] => {
  const [startTime, endTime] = clipboardDayValue
  const daysISO = days.map(d => d.isoDate)
  const values = days.map(({ isoDate }) => {
    const startDate = startTime ? timeInputValueToDate(new Date(isoDate), startTime) : undefined
    const endDate = endTime ? timeInputValueToDate(new Date(isoDate), endTime) : undefined
    return {
      start: startDate ? { date: startDate } : undefined,
      end: endDate ? { date: endDate } : undefined,
    }
  })
  return updateWorkPeriods(workPeriods, daysISO, values)
}

/** Build the payload to copy for a full week (one slot per day, null if no work period). */
export const getWeekCopyPayload = (week: (DayWithIsoDate | null)[]): WeekCopySlot[] => {
  return week.map(day => {
    if (!day) return null
    const startDate = day?.data?.start?.date
    const endDate = day?.data?.end?.date
    if (!startDate || !endDate) return null
    return [dateToTimeInputValue(startDate), dateToTimeInputValue(endDate)] as const
  })
}

/** Build work periods after pasting a week from clipboard (clipboardWeek aligned by index with week). */
export const getWorkPeriodsAfterPasteWeek = (
  workPeriods: MissionFormValues['workPeriods'],
  week: ({ isoDate: string } | null)[],
  clipboardWeek: ([string, string] | null)[],
): MissionFormValues['workPeriods'] => {
  const res = clipboardWeek
    .map((clipboardDay, index) => {
      const dayInWeek = week[index]
      if (!dayInWeek) return null
      const { isoDate } = dayInWeek
      if (clipboardDay === null)
        return { isoDate, value: undefined as MissionFormValues['workPeriods'][0] | undefined }
      const [start, end] = clipboardDay
      const startDate = timeInputValueToDate(new Date(isoDate), start)
      const endDate = timeInputValueToDate(new Date(isoDate), end)
      if (!startDate || !endDate)
        return { isoDate, value: undefined as MissionFormValues['workPeriods'][0] | undefined }
      return {
        isoDate,
        value: {
          start: { date: startDate },
          end: { date: endDate },
        },
      }
    })
    .filter((v): v is NonNullable<typeof v> => v !== null)
  const daysISO = res.map(d => d.isoDate)
  const values = res.map(d => d.value)
  return updateWorkPeriods(workPeriods, daysISO, values)
}

/** Build the payload to copy for a single day, or null if day has no start/end. */
export const getDayCopyPayload = (day: DayWithIsoDate | null): TimePair | null => {
  if (!day) return null
  const startDate = day.data?.start?.date
  const endDate = day.data?.end?.date
  if (!startDate || !endDate) return null
  return [dateToTimeInputValue(startDate), dateToTimeInputValue(endDate)]
}

/** Build work periods after pasting a day value onto one isoDate. */
export const getWorkPeriodsAfterPasteDay = (
  workPeriods: MissionFormValues['workPeriods'],
  isoDate: string,
  clipboardDayValue: TimePair,
): MissionFormValues['workPeriods'] => {
  const [startTime, endTime] = clipboardDayValue
  const startDate = timeInputValueToDate(new Date(isoDate), startTime)
  const endDate = timeInputValueToDate(new Date(isoDate), endTime)
  return updateWorkPeriod(workPeriods, isoDate, {
    ...(startDate ? { start: { date: startDate } } : {}),
    ...(endDate ? { end: { date: endDate } } : {}),
  })
}
